import { PERMISSIONS } from '_constants';
import HasAccess from 'modules/AccessControl/hasAccess';
import IsAzAccount from 'modules/AccessControl/isAzAccount';
import IsFlAccount from 'modules/AccessControl/isFlAccount';

// P20M-1308 — central visibility map for query-builder filter groups, scoped per page.
//
// The same filter group (Products sold, Store ownership, Other retailer details, …) is rendered on
// several page types, and the ticket requires hiding it independently per page. So visibility is
// resolved per page: `useHiddenFilters(page)` where `page` is one of FILTER_PAGES. A group is shown
// only when the user holds the single master `view_all_hidden_filters` permission (Multi-Account
// Managers) OR that page's own per-group permission. Every returned `canView*` flag is `true` when
// the group SHOULD be shown, and the flag NAMES are identical across pages so consumers don't change
// — they only pass their page (Retailer is the default).
//
// AVC agreement filters are special: they stay visible only in the AZ and FL accounts (by
// subdomain) and are intentionally NOT covered by the master override — `canViewAvc` reflects that.
//
// NOTE: HasAccess is a hook, so every call below must run unconditionally on every render (no
// short-circuiting). We resolve every permission first, then pick the requested page's subset.
export const FILTER_PAGES = {
  RETAILER: 'retailer',
  EDUCATION: 'education',
  ENFORCEMENT: 'enforcement',
  ASSESSMENT: 'assessment',
};

// Maps a visit-list route to its FILTER_PAGES key. Returns undefined for anything that is not a
// visit list (e.g. the retailer list), so callers fall back to the hook's default. An unknown page
// yields an empty flag set, i.e. every group hidden — the safe default.
export const resolveVisitPage = (pathname = '') => {
  if (pathname.includes('/enforcement')) return FILTER_PAGES.ENFORCEMENT;
  if (pathname.includes('/education')) return FILTER_PAGES.EDUCATION;
  if (pathname.includes('/assessment')) return FILTER_PAGES.ASSESSMENT;
  return undefined;
};

const useHiddenFilters = (page = FILTER_PAGES.RETAILER) => {
  const canViewAll = HasAccess(PERMISSIONS.VIEW_ALL_HIDDEN_FILTERS);

  // Retailer list (original page-less permissions).
  const rTobacco = HasAccess(PERMISSIONS.VIEW_FILTER_PRODUCTS_SOLD_TOBACCO);
  const rAlcohol = HasAccess(PERMISSIONS.VIEW_FILTER_PRODUCTS_SOLD_ALCOHOL);
  const rStoreType = HasAccess(PERMISSIONS.VIEW_FILTER_STORE_TYPE);
  // P20M-1308 follow-up — Store ownership on the retailer list is hidden by default too.
  const rStoreOwnership = HasAccess(PERMISSIONS.VIEW_FILTER_STORE_OWNERSHIP);
  const rOtherRetailerDetails = HasAccess(PERMISSIONS.VIEW_FILTER_OTHER_RETAILER_DETAILS);
  const rAdvanced = HasAccess(PERMISSIONS.VIEW_FILTER_ADVANCED);

  // Education visit list.
  const eduCompletion = HasAccess(PERMISSIONS.VIEW_FILTER_EDUCATION_VISIT_COMPLETION_STATUS);
  const eduCompliance = HasAccess(PERMISSIONS.VIEW_FILTER_EDUCATION_COMPLIANCE_INSPECTION);
  const eduDiscussion = HasAccess(PERMISSIONS.VIEW_FILTER_EDUCATION_DISCUSSION);
  const eduTobacco = HasAccess(PERMISSIONS.VIEW_FILTER_EDUCATION_PRODUCTS_SOLD_TOBACCO);
  const eduAlcohol = HasAccess(PERMISSIONS.VIEW_FILTER_EDUCATION_PRODUCTS_SOLD_ALCOHOL);
  const eduStoreOwnership = HasAccess(PERMISSIONS.VIEW_FILTER_EDUCATION_STORE_OWNERSHIP);
  const eduOtherRetailerDetails = HasAccess(PERMISSIONS.VIEW_FILTER_EDUCATION_OTHER_RETAILER_DETAILS);

  // Enforcement / inspection visit list.
  const enfCompletion = HasAccess(PERMISSIONS.VIEW_FILTER_ENFORCEMENT_VISIT_COMPLETION_STATUS);
  const enfAgeVerification = HasAccess(PERMISSIONS.VIEW_FILTER_ENFORCEMENT_AGE_VERIFICATION);
  const enfOtherVisitDetails = HasAccess(PERMISSIONS.VIEW_FILTER_ENFORCEMENT_OTHER_VISIT_DETAILS);
  const enfTobacco = HasAccess(PERMISSIONS.VIEW_FILTER_ENFORCEMENT_PRODUCTS_SOLD_TOBACCO);
  const enfAlcohol = HasAccess(PERMISSIONS.VIEW_FILTER_ENFORCEMENT_PRODUCTS_SOLD_ALCOHOL);
  const enfStoreOwnership = HasAccess(PERMISSIONS.VIEW_FILTER_ENFORCEMENT_STORE_OWNERSHIP);
  const enfOtherRetailerDetails = HasAccess(PERMISSIONS.VIEW_FILTER_ENFORCEMENT_OTHER_RETAILER_DETAILS);

  // Assessment visit list.
  const assCompletion = HasAccess(PERMISSIONS.VIEW_FILTER_ASSESSMENT_VISIT_COMPLETION_STATUS);
  const assResults = HasAccess(PERMISSIONS.VIEW_FILTER_ASSESSMENT_RESULTS);
  const assTobacco = HasAccess(PERMISSIONS.VIEW_FILTER_ASSESSMENT_PRODUCTS_SOLD_TOBACCO);
  const assAlcohol = HasAccess(PERMISSIONS.VIEW_FILTER_ASSESSMENT_PRODUCTS_SOLD_ALCOHOL);
  const assStoreOwnership = HasAccess(PERMISSIONS.VIEW_FILTER_ASSESSMENT_STORE_OWNERSHIP);
  const assOtherRetailerDetails = HasAccess(PERMISSIONS.VIEW_FILTER_ASSESSMENT_OTHER_RETAILER_DETAILS);

  const isAzAccount = IsAzAccount();
  const isFlAccount = IsFlAccount();

  const byPage = {
    [FILTER_PAGES.RETAILER]: {
      canViewProductsSoldTobacco: rTobacco,
      canViewProductsSoldAlcohol: rAlcohol,
      canViewStoreType: rStoreType,
      // Same flag name the visit lists use for their own store-ownership permission, per the note above.
      canViewStoreOwnership: rStoreOwnership,
      canViewOtherRetailerDetails: rOtherRetailerDetails,
      canViewAdvancedFilters: rAdvanced,
    },
    [FILTER_PAGES.EDUCATION]: {
      canViewVisitCompletionStatus: eduCompletion,
      canViewComplianceInspection: eduCompliance,
      canViewEducationDiscussion: eduDiscussion,
      canViewProductsSoldTobacco: eduTobacco,
      canViewProductsSoldAlcohol: eduAlcohol,
      canViewStoreOwnership: eduStoreOwnership,
      canViewOtherRetailerDetails: eduOtherRetailerDetails,
    },
    [FILTER_PAGES.ENFORCEMENT]: {
      canViewVisitCompletionStatus: enfCompletion,
      canViewAgeVerification: enfAgeVerification,
      canViewOtherVisitDetails: enfOtherVisitDetails,
      canViewProductsSoldTobacco: enfTobacco,
      canViewProductsSoldAlcohol: enfAlcohol,
      canViewStoreOwnership: enfStoreOwnership,
      canViewOtherRetailerDetails: enfOtherRetailerDetails,
    },
    [FILTER_PAGES.ASSESSMENT]: {
      canViewVisitCompletionStatus: assCompletion,
      canViewAssessmentResults: assResults,
      canViewProductsSoldTobacco: assTobacco,
      canViewProductsSoldAlcohol: assAlcohol,
      canViewStoreOwnership: assStoreOwnership,
      canViewOtherRetailerDetails: assOtherRetailerDetails,
    },
  };

  const scoped = byPage[page] || {};

  // Each per-group flag is shown when the master override is held OR the page's own permission is.
  return Object.keys(scoped).reduce(
    (acc, key) => {
      acc[key] = canViewAll || scoped[key];
      return acc;
    },
    {
      canViewAll,
      canViewAvc: isAzAccount || isFlAccount,
    },
  );
};

export default useHiddenFilters;
